import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Progress } from './ui/progress';
import { Button } from './ui/button';

/**
 * AI damage assessment progress strip.
 *
 * Deliberately a strip and not a modal or an overlay: the assessment runs for
 * minutes at a time and the assessor must stay free to write the quote while it
 * does. Nothing here disables anything.
 *
 * The same reasoning drives the stall notice. A run that dies mid-flight leaves
 * the record saying "processing" forever, so once the orchestrator's heartbeat
 * goes quiet the strip offers a way out rather than trapping the record.
 */

// The orchestrator heartbeats at least every 20s; three minutes of silence means
// the run is gone, not slow.
const STALE_AFTER_MS = 3 * 60 * 1000;

const STAGES = {
  queued: { label: 'Getting started', hint: 'Warming up the assessment' },
  photos: { label: 'Analysing photos', hint: 'Detecting panels and damage in each photo' },
  assessing: { label: 'Assessing damage', hint: 'Working out what each panel needs' },
  aggregating: { label: 'Building the assessment', hint: 'Matching parts to insurer line items' },
  done: { label: 'Finishing up', hint: 'Writing the results' },
  failed: { label: 'Assessment failed', hint: 'Nothing was written — you can retry below' },
};

const AssessmentProgress = ({
  progress = 0,
  stage = null,
  heartbeat = null,
  startedAt = null,
  onStopWaiting,
}) => {
  // Staleness is time-based, so re-evaluate on a timer rather than only on props.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);

  const lastSign = heartbeat || startedAt;
  const silentFor = lastSign ? now - new Date(lastSign).getTime() : 0;
  const isStale = Boolean(lastSign) && silentFor > STALE_AFTER_MS;

  const info = STAGES[stage] || STAGES.queued;
  const pct = Math.max(0, Math.min(100, Number(progress) || 0));
  // Before the first report there is no honest number to show.
  const indeterminate = !isStale && pct <= 0;

  return (
    <div className="rounded-md border bg-muted/40 px-3 py-2.5 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {isStale ? (
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
        )}
        <span className="text-sm font-medium">
          {isStale ? 'The assessment has stopped responding' : info.label}
        </span>
        {!isStale && (
          <span className="text-xs text-muted-foreground">
            {indeterminate ? info.hint : `${Math.round(pct)}% — ${info.hint}`}
          </span>
        )}
        <span className="flex-1" />
        {isStale && onStopWaiting && (
          <Button variant="outline" size="sm" onClick={onStopWaiting}>
            Stop waiting
          </Button>
        )}
      </div>

      <Progress
        value={pct}
        indeterminate={indeterminate}
        className={isStale ? 'opacity-40' : undefined}
      />
    </div>
  );
};

export default AssessmentProgress;
